import {
  Card,
  FlatActionList,
  Text,
  Page,
  Stack,
  Button,
  TileButtonActionList,
  ProductThumbnail,
  Badge,
  useFlashMessages,
} from "@towersystems/roam-common-native";
import { useInvoiceArrivalContextProvider } from "../../utils/active-invoice-arrival";
import { StackScreenProps } from "@react-navigation/stack";
import { EditInvoiceArrivalRootParamList } from "../../../../types";
import {
  HTML5_FMT,
  moneyToFormattedString,
} from "@towersystems/roam-common/lib/shared-utils";
import React from "react";
import { Spacings, View } from "react-native-ui-lib";
import { useMutation } from "@apollo/client";
import {
  MUTATION_CANCEL_INVOICE_ARRIVAL,
  MUTATION_COMPLETE_INVOICE_ARRIVAL,
} from "../../utils";
import { InvoiceArrival } from "@towersystems/roam-common/lib/generated-types";
import { formatDate } from "../../../../utilities/date/format-date";
import { ConfirmationPrompt } from "../../../../components/ConfirmationPrompt";
import { useTranslation } from "../../../../utilities/i18n";
import { Spacer } from "../../../../components/Spacer";

export type EditInvoiceArrivalDetailsScreenProps =
  StackScreenProps<EditInvoiceArrivalRootParamList>;

export const EditInvoiceArrivalDetailsScreen = ({
  navigation,
}: EditInvoiceArrivalDetailsScreenProps) => {
  const { t } = useTranslation("invoice_arrival");
  const [promptVisible, setPromptVisible] = React.useState(false);

  const { invoiceArrival, setInvoiceArrival } =
    useInvoiceArrivalContextProvider();

  const [cancelInvoiceMutation, { loading: cancellingInvoice }] = useMutation<{
    cancelInvoiceArrival: InvoiceArrival;
  }>(MUTATION_CANCEL_INVOICE_ARRIVAL);

  const [completeInvoiceMutation, { loading: completingInvoice }] =
    useMutation<{
      completeInvoiceArrival: InvoiceArrival;
    }>(MUTATION_COMPLETE_INVOICE_ARRIVAL);

  const flashMessagesContext = useFlashMessages();

  const handleOnComplete = React.useCallback(async () => {
    try {
      const result = await completeInvoiceMutation({
        variables: {
          id: invoiceArrival.id,
        },
      });

      if (result.data?.completeInvoiceArrival) {
        setInvoiceArrival(result.data.completeInvoiceArrival);
        navigation.goBack();
      }
    } catch (err: any) {
      flashMessagesContext.showMessage({
        message: err.message ?? t("common:error"),
        severity: "error",
      });
    }
  }, [invoiceArrival]);

  const handleOnCancel = React.useCallback(async () => {
    try {
      const result = await cancelInvoiceMutation({
        variables: {
          id: invoiceArrival.id,
        },
      });

      if (result.data?.cancelInvoiceArrival) {
        setPromptVisible(false);
        setInvoiceArrival(result.data.cancelInvoiceArrival);
        navigation.goBack();
      }
    } catch (err: any) {
      flashMessagesContext.showMessage({
        message: err.message ?? t("common:error"),
        severity: "error",
      });
    }
  }, [invoiceArrival]);

  let actions = [
    {
      isAction: true,
      id: "complete",
      label: t("action.complete_invoice.label"),
      disabled: invoiceArrival.items.length === 0 || completingInvoice,
      children: (
        <View style={{ alignItems: "center" }}>
          <Text
            color="subdued"
            textAlign="center"
            style={{ textAlign: "center" }}
          >
            {t("action.complete_invoice.description")}
          </Text>
        </View>
      ),
      onPress: handleOnComplete,
    },
    {
      isDestructive: true,
      id: "cancel",
      label: t("action.cancel_invoice.label"),
      onPress: () => setPromptVisible(true),
      children: (
        <View style={{ alignItems: "center" }}>
          <Text color="subdued" style={{ textAlign: "center" }}>
            {t("action.cancel_invoice.description")}
          </Text>
        </View>
      ),
      disabled: cancellingInvoice,
    },
  ];

  const sortedItems = React.useMemo(
    () =>
      invoiceArrival.items.slice().sort((a, b) => {
        const createdAtA = new Date(a.createdAt);
        const createdAtB = new Date(b.createdAt);
        //@ts-ignore
        return createdAtB - createdAtA;
      }),
    [invoiceArrival.items]
  );

  const actionsMarkup =
    actions.length >= 1 ? (
      <View>
        <TileButtonActionList actions={actions} />
      </View>
    ) : null;

  const cancelPrompt = (
    <ConfirmationPrompt
      visible={promptVisible}
      title={t("prompt.cancel_invoice.title")}
      description={t("prompt.cancel_invoice.description")}
      onCancelPress={() => setPromptVisible(false)}
      onConfirmPress={handleOnCancel}
      disabled={cancellingInvoice}
    />
  );

  return (
    <Page
      title={t("edit_invoice_title")}
      badges={
        <Stack spacing={Spacings.s2}>
          <Badge invoiceArrivalStatus={invoiceArrival.status}></Badge>
        </Stack>
      }
    >
      <Stack vertical>
        {actionsMarkup}
        <Card title={t("card.details.title")}>
          <FlatActionList
            items={[
              {
                key: "supplier",
                title: t("form.supplier.label"),
                subTitle: t("form.supplier.description"),
                componentRight: () => (
                  <Text>
                    {invoiceArrival.supplier?.name ??
                      t("common:not_applicable")}
                  </Text>
                ),
              },
              {
                key: "location",
                title: t("form.location.label"),
                subTitle: t("form.location.description"),
                componentRight: () => (
                  <Text>
                    {invoiceArrival.location?.name ??
                      t("common:not_applicable")}
                  </Text>
                ),
              },
              {
                key: "invoice_number",
                title: t("form.invoice_number.label"),
                subTitle: t("form.invoice_number.description"),

                onPress: () =>
                  navigation.navigate("GenericEditFormInputScreen", {
                    render: "invoice_number",
                  }),
                componentRight: () => (
                  <Text color="action" fontWeight="bold">
                    {invoiceArrival.invoiceNumber
                      ? t("formatted_invoice_number", {
                          invoice_number: invoiceArrival.invoiceNumber,
                        })
                      : t("common:not_applicable")}
                  </Text>
                ),
              },

              {
                key: "order_number",
                title: t("form.order_number.label"),
                subTitle: t("form.order_number.description"),

                onPress: () =>
                  navigation.navigate("GenericEditFormInputScreen", {
                    render: "order_number",
                  }),
                componentRight: () => (
                  <Text color="action" fontWeight="bold">
                    {invoiceArrival.orderNumber
                      ? t("formatted_invoice_number", {
                          invoice_number: invoiceArrival.orderNumber,
                        })
                      : t("common:not_applicable")}
                  </Text>
                ),
              },

              {
                key: "invoice_date",
                title: t("form.invoice_date.label"),
                subTitle: t("form.invoice_date.description"),
                onPress: () =>
                  navigation.navigate("GenericEditFormInputScreen", {
                    render: "invoice_date",
                  }),
                componentRight: () => (
                  <Text color="action" fontWeight="bold">
                    {invoiceArrival.invoiceDate
                      ? `${formatDate(
                          invoiceArrival.invoiceDate,
                          HTML5_FMT.DATE
                        )}`
                      : t("form.invoice_date.placeholder")}
                  </Text>
                ),
              },

              {
                key: "arrival_date",
                title: t("form.arrival_date.label"),
                subTitle: t("form.arrival_date.description"),
                onPress: () =>
                  navigation.navigate("GenericEditFormInputScreen", {
                    render: "arrival_date",
                  }),
                componentRight: () => (
                  <Text color="action" fontWeight="bold">
                    {invoiceArrival.arrivalDate
                      ? `${formatDate(
                          invoiceArrival.arrivalDate,
                          HTML5_FMT.DATE
                        )}`
                      : t("form.arrival_date.placeholder")}
                  </Text>
                ),
              },
              {
                key: "invoice_due_date",
                title: t("form.invoice_due_date.label"),
                subTitle: t("form.invoice_due_date.description"),
                onPress: () =>
                  navigation.navigate("GenericEditFormInputScreen", {
                    render: "invoice_due_date",
                  }),
                componentRight: () => (
                  <Text color="action" fontWeight="bold">
                    {invoiceArrival.dueDate
                      ? `${formatDate(invoiceArrival.dueDate, HTML5_FMT.DATE)}`
                      : t("form.invoice_due_date.placeholder")}
                  </Text>
                ),
              },
              {
                key: "invoice_freight",
                title: t("form.invoice_freight.label"),
                subTitle: t("form.invoice_freight.description"),
                onPress: () =>
                  navigation.navigate("GenericEditFormInputScreen", {
                    render: "invoice_freight",
                  }),
                componentRight: () => (
                  <Text color="action" fontWeight="bold">
                    {invoiceArrival.freightCost
                      ? `${moneyToFormattedString(invoiceArrival.freightCost)}`
                      : t("form.invoice_freight.placeholder")}
                  </Text>
                ),
              },
              {
                key: "invoice_cost_price_inc_gst",
                title: t("form.invoice_cost_price_inc_gst.label"),
                subTitle: t("form.invoice_cost_price_inc_gst.description"),
                onPress: () =>
                  navigation.navigate("GenericEditFormInputScreen", {
                    render: "invoice_cost_price_inc_gst",
                  }),
                componentRight: () => (
                  <Text color="action" fontWeight="bold">
                    {invoiceArrival.costPriceIncGST ? "Yes" : "No"}
                  </Text>
                ),
              },
            ]}
          />
        </Card>
        <Card title={t("card.products.title")}>
          <Stack vertical>
            <Button
              isAction
              label={t("button.add_product")}
              onPress={() => navigation.navigate("AddProductModalRootScreen")}
            ></Button>
            {sortedItems.length >= 1 && (
              <FlatActionList
                style={{ overflow: "visible" }}
                items={sortedItems.map((item) => ({
                  key: item.id,
                  titleContent({ title, subTitle }) {
                    return (
                      <View flex row>
                        <View
                          style={{
                            alignItems: "center",
                            flexBasis: "100%",
                            flexShrink: 1,
                          }}
                          row
                        >
                          <ProductThumbnail
                            imageUrl={item.product?.image?.path}
                            quantity={item.quantity}
                            isInternalProduct={false}
                            size="large"
                          />
                          <View marginL-20 style={{ flexShrink: 1 }}>
                            <Text variant="heading2">
                              {item.product?.name ??
                                item?.name ??
                                t("common:unknown_resource")}
                            </Text>
                            {subTitle}
                          </View>
                        </View>
                      </View>
                    );
                  },
                  title: "",
                  subTitle: item.barcode,
                  onPress: () =>
                    navigation.navigate("EditInvoiceLineItemRootScreen", {
                      screen: "EditInvoiceLineItemScreen",
                      lineItemId: item.id,
                    }),
                  componentRight: (icon) => (
                    <Stack style={{ marginLeft: Spacings.s4 * 2 }} spacing={1}>
                      <View style={{ alignItems: "flex-end" }}>
                        <Text variant="heading1" textAlign="right">
                          {moneyToFormattedString(item.totalCost)}
                        </Text>
                        <Text color="subdued" textAlign="right">
                          {`${moneyToFormattedString(item.costPrice)} EA`}
                        </Text>
                      </View>
                      {icon}
                    </Stack>
                  ),
                }))}
              ></FlatActionList>
            )}
          </Stack>
        </Card>
        {invoiceArrival.items.length >= 1 && (
          <Card title="Totals">
            <FlatActionList
              items={[
                {
                  key: "quantity",
                  title: t("form.total_quantity.label"),
                  componentRight: () => (
                    <Text variant="heading1">
                      {invoiceArrival.totalQuantity}
                    </Text>
                  ),
                },

                {
                  key: "total_gst",
                  title: t("form.total_invoice_gst_credit.label"),
                  componentRight: () => (
                    <Text variant="heading1">
                      {moneyToFormattedString(
                        invoiceArrival.formattedGSTCredit
                      )}
                    </Text>
                  ),
                },

                {
                  key: "total_cost",
                  title: t("form.total_cost.label"),
                  componentRight: () => (
                    <Text variant="heading1">
                      {moneyToFormattedString(invoiceArrival.totalCost)}
                    </Text>
                  ),
                },

                {
                  key: "total_inc_gst",
                  title: t("form.total_cost_inc_gst.label"),
                  componentRight: () => (
                    <Text variant="heading1">
                      {moneyToFormattedString(invoiceArrival.totalCostIncGST)}
                    </Text>
                  ),
                },
              ]}
            ></FlatActionList>
          </Card>
        )}
      </Stack>
      {cancelPrompt}
      <Spacer />
    </Page>
  );
};
